import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";
import { useSuspenseQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Clock, MessageCircleQuestion, Share2 } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { AskQuestionModal } from "@/components/academy/AskQuestionModal";
import { LessonCallout } from "@/components/academy/LessonCallout";
import { getLesson, getTrackBySlug } from "@/lib/academy.functions";
import {
  ensureSessionBootstrap,
  fetchCompletedLessonIds,
  markLessonComplete,
} from "@/lib/academy-progress";
import { cn } from "@/lib/utils";

function extractText(node: ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(extractText).join("");
  if (typeof node === "object" && "props" in (node as { props?: unknown })) {
    return extractText((node as { props: { children?: ReactNode } }).props?.children);
  }
  return "";
}

export const Route = createFileRoute(
  "/academy/tracks/$trackSlug/lessons/$lessonSlug",
)({
  loader: async ({ params, context }) => {
    const data = await context.queryClient.ensureQueryData({
      queryKey: ["academy", "lesson", params.trackSlug, params.lessonSlug],
      queryFn: () =>
        getLesson({
          data: { trackSlug: params.trackSlug, lessonSlug: params.lessonSlug },
        }),
    });
    if (!data) throw notFound();
    const trackData = await context.queryClient.ensureQueryData({
      queryKey: ["academy", "track", params.trackSlug],
      queryFn: () => getTrackBySlug({ data: { slug: params.trackSlug } }),
    });
    return {
      lessonTitle: data.lesson?.title ?? params.lessonSlug.replace(/-/g, " "),
      lessonSubtitle: data.lesson?.subtitle ?? null,
      lessonExcerpt: data.lesson?.excerpt ?? null,
      trackTitle: trackData?.track?.title ?? data.track?.title ?? null,
      datePublished: data.lesson?.created_at ?? null,
      dateModified: data.lesson?.updated_at ?? data.lesson?.created_at ?? null,
    };
  },
  head: ({ params, loaderData }) => {
    const title = loaderData?.lessonTitle ?? params.lessonSlug.replace(/-/g, " ");
    const raw =
      loaderData?.lessonExcerpt ||
      loaderData?.lessonSubtitle ||
      `${title} — a lesson in the Currency Thoughts Academy on building generational wealth.`;
    const description = raw.length > 160 ? `${raw.slice(0, 157)}…` : raw;
    const canonical = `https://currencythoughts.net/academy/tracks/${params.trackSlug}/lessons/${params.lessonSlug}`;
    const site = "https://currencythoughts.net";
    const trackTitle =
      loaderData?.trackTitle ?? params.trackSlug.replace(/-/g, " ");
    const article: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: title,
      description,
      mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
      author: {
        "@type": "Organization",
        name: "Currency Thoughts LLC",
        url: site,
      },
      publisher: {
        "@type": "Organization",
        name: "Currency Thoughts LLC",
        url: site,
        logo: {
          "@type": "ImageObject",
          url: `${site}/og-seal.png`,
        },
      },
    };
    if (loaderData?.datePublished) article.datePublished = loaderData.datePublished;
    if (loaderData?.dateModified) article.dateModified = loaderData.dateModified;

    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: site },
        { "@type": "ListItem", position: 2, name: "Academy", item: `${site}/academy` },
        {
          "@type": "ListItem",
          position: 3,
          name: trackTitle,
          item: `${site}/academy/tracks/${params.trackSlug}`,
        },
        { "@type": "ListItem", position: 4, name: title, item: canonical },
      ],
    };
    return {
      meta: [
        { title: `${title} — Currency Thoughts Academy` },
        { name: "description", content: description },
        { property: "og:title", content: `${title} — Currency Thoughts Academy` },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: canonical },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: `${title} — Currency Thoughts Academy` },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(article) },
        { type: "application/ld+json", children: JSON.stringify(breadcrumb) },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="flex min-h-screen flex-col bg-cream-50">
      <Navbar />
      <main className="flex-1 pt-32 px-5 text-center">
        <h1 className="font-display text-3xl text-navy-900">Lesson not found</h1>
        <Link to="/academy" className="mt-4 inline-block text-bronze-600">
          ← Back to Academy
        </Link>
      </main>
      <Footer />
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="p-10 font-sans text-charcoal-900">Error: {error.message}</div>
  ),
  component: LessonPage,
});

function LessonPage() {
  const { trackSlug, lessonSlug } = Route.useParams();
  const qc = useQueryClient();
  const [askOpen, setAskOpen] = useState(false);
  const [marking, setMarking] = useState(false);

  useEffect(() => { ensureSessionBootstrap(); }, []);

  const { data } = useSuspenseQuery({
    queryKey: ["academy", "lesson", trackSlug, lessonSlug],
    queryFn: () => getLesson({ data: { trackSlug, lessonSlug } }),
  });
  const { data: trackData } = useSuspenseQuery({
    queryKey: ["academy", "track", trackSlug],
    queryFn: () => getTrackBySlug({ data: { slug: trackSlug } }),
  });
  const outline = trackData?.lessons ?? [];
  const outlineIds = outline.map((l) => l.id);
  const { data: completed } = useQuery({
    queryKey: ["academy", "progress", trackSlug, outlineIds.length],
    queryFn: () => fetchCompletedLessonIds(outlineIds),
    enabled: outlineIds.length > 0,
  });

  if (!data) return null;
  const { track, lesson, prev, next } = data;
  const completedSet = completed ?? new Set<string>();
  const isDone = completedSet.has(lesson.id);
  const idx = outline.findIndex((l) => l.slug === lesson.slug);
  const position = idx >= 0 ? idx + 1 : 1;

  async function handleComplete() {
    setMarking(true);
    try {
      await markLessonComplete(lesson.id);
      toast.success("Lesson complete");
      await qc.invalidateQueries({ queryKey: ["academy", "progress"] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not save progress");
    } finally {
      setMarking(false);
    }
  }

  async function handleShare() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy link");
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-cream-50">
      <Navbar />
      <main className="flex-1 pt-20">
        <section className="bg-navy-900 text-cream-50">
          <div className="mx-auto max-w-6xl px-5 py-14 md:px-10 md:py-20">
            <nav className="font-sans text-[12px] uppercase tracking-[0.2em] text-bronze-400">
              <Link to="/academy" className="hover:text-bronze-400/80">Academy</Link>
              <span className="px-2 text-cream-50/40">/</span>
              <Link to="/academy/tracks/$slug" params={{ slug: track.slug }} className="hover:text-bronze-400/80">
                {track.title}
              </Link>
            </nav>
            <div className="mt-6">
              <Eyebrow color="cream">
                Lesson {String(position).padStart(2, "0")} of {String(outline.length).padStart(2, "0")}
              </Eyebrow>
            </div>
            <h1 className="mt-6 max-w-3xl font-display text-[36px] leading-tight md:text-[52px]">
              {lesson.title}
            </h1>
            {lesson.subtitle && (
              <p className="mt-3 font-display text-[18px] italic text-bronze-400">
                {lesson.subtitle}
              </p>
            )}
            <p className="mt-6 flex items-center gap-2 font-sans text-[12px] uppercase tracking-[0.18em] text-cream-50/70">
              <Clock className="h-3 w-3" />
              {lesson.estimated_minutes} min · {lesson.lesson_type}
            </p>
          </div>
        </section>

        <div className="mx-auto grid max-w-6xl gap-12 px-5 py-16 md:px-10 lg:grid-cols-[1fr_260px]">
          <article>
            <div
              className="prose prose-lg max-w-none font-sans text-charcoal-900
                prose-headings:font-display prose-headings:text-navy-900
                prose-h2:text-[28px] prose-h2:mt-10
                prose-h3:text-[20px] prose-h3:mt-8
                prose-p:leading-[1.75] prose-strong:text-navy-900
                prose-li:my-1 prose-ul:my-4
                prose-blockquote:border-l-2 prose-blockquote:border-bronze-600
                prose-blockquote:not-italic prose-blockquote:text-charcoal-900
                first-letter:prose-p:first-of-type:font-display
                first-letter:prose-p:first-of-type:text-[64px]
                first-letter:prose-p:first-of-type:float-left
                first-letter:prose-p:first-of-type:leading-[0.9]
                first-letter:prose-p:first-of-type:mr-3
                first-letter:prose-p:first-of-type:mt-1
                first-letter:prose-p:first-of-type:text-bronze-600"
            >
              <ReactMarkdown
                components={{
                  blockquote: ({ children, ...props }) => {
                    const text = extractText(children);
                    if (/^\s*NOTE:/i.test(text)) {
                      return (
                        <LessonCallout>
                          {text.replace(/^\s*NOTE:\s*/i, "")}
                        </LessonCallout>
                      );
                    }
                    return <blockquote {...props}>{children}</blockquote>;
                  },
                }}
              >
                {lesson.body ?? ""}
              </ReactMarkdown>
            </div>

            <p className="mt-12 border-t border-bronze-600/30 pt-6 font-sans text-[12px] italic text-charcoal-500">
              Educational content only. Nothing in this lesson constitutes legal, tax, or
              investment advice. Insurance products are governed by the policy contract
              issued by the carrier.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <PrimaryButton onClick={handleComplete} disabled={marking || isDone} size="lg">
                {isDone ? (<><Check className="h-4 w-4" /> Completed</>) : marking ? "Saving..." : "Mark Complete"}
              </PrimaryButton>
              <button
                type="button"
                onClick={() => setAskOpen(true)}
                className="inline-flex items-center gap-2 border border-bronze-600/40 bg-transparent px-5 py-3 font-sans text-[12px] font-semibold uppercase tracking-[0.05em] text-navy-900 transition-colors hover:bg-bronze-600/10"
                style={{ borderRadius: 4 }}
              >
                <MessageCircleQuestion className="h-4 w-4 text-bronze-600" />
                Ask about this lesson
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex items-center gap-2 px-3 py-3 font-sans text-[12px] font-semibold uppercase tracking-[0.05em] text-charcoal-500 transition-colors hover:text-navy-900"
              >
                <Share2 className="h-4 w-4" /> Share
              </button>
            </div>

            <nav className="mt-12 grid gap-4 sm:grid-cols-2">
              {prev ? (
                <Link
                  to="/academy/tracks/$trackSlug/lessons/$lessonSlug"
                  params={{ trackSlug: track.slug, lessonSlug: prev.slug }}
                  className="group border border-bronze-600/30 bg-white p-5 transition-colors hover:border-bronze-600"
                  style={{ borderRadius: 4 }}
                >
                  <span className="flex items-center gap-2 font-sans text-[10px] uppercase tracking-[0.2em] text-charcoal-500">
                    <ArrowLeft className="h-3 w-3 transition-transform group-hover:-translate-x-1" /> Previous
                  </span>
                  <p className="mt-2 font-display text-[18px] text-navy-900">{prev.title}</p>
                </Link>
              ) : <span />}
              {next ? (
                <Link
                  to="/academy/tracks/$trackSlug/lessons/$lessonSlug"
                  params={{ trackSlug: track.slug, lessonSlug: next.slug }}
                  className="group border border-bronze-600/30 bg-white p-5 text-right transition-colors hover:border-bronze-600"
                  style={{ borderRadius: 4 }}
                >
                  <span className="flex items-center justify-end gap-2 font-sans text-[10px] uppercase tracking-[0.2em] text-charcoal-500">
                    Next <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-1" />
                  </span>
                  <p className="mt-2 font-display text-[18px] text-navy-900">{next.title}</p>
                </Link>
              ) : <span />}
            </nav>
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-28">
              <p className="font-sans text-[11px] font-semibold uppercase tracking-[0.22em] text-bronze-600">
                Track Outline
              </p>
              <ol className="mt-4 space-y-1 border-l border-bronze-600/30">
                {outline.map((l, i) => {
                  const active = l.slug === lesson.slug;
                  const done = completedSet.has(l.id);
                  return (
                    <li key={l.id}>
                      <Link
                        to="/academy/tracks/$trackSlug/lessons/$lessonSlug"
                        params={{ trackSlug: track.slug, lessonSlug: l.slug }}
                        className={cn(
                          "block border-l-2 -ml-px py-2 pl-4 pr-2 font-sans text-[13px] leading-snug transition-colors",
                          active
                            ? "border-bronze-600 bg-bronze-600/5 text-navy-900 font-semibold"
                            : "border-transparent text-charcoal-500 hover:text-navy-900 hover:border-bronze-600/40",
                        )}
                      >
                        <span className="mr-2 text-bronze-600">
                          {done ? "✓" : String(i + 1).padStart(2, "0")}
                        </span>
                        {l.title}
                      </Link>
                    </li>
                  );
                })}
              </ol>
            </div>
          </aside>
        </div>
      </main>
      <Footer />
      <AskQuestionModal
        open={askOpen}
        onClose={() => setAskOpen(false)}
        contextLabel={`${track.title} → ${lesson.title}`}
      />
    </div>
  );
}